import {
  AccountCircle,
  KeyboardArrowDown,
  KeyboardArrowUp,
  LocalPhone,
} from '@mui/icons-material'
import { debounce, IconButton } from '@mui/material'
import { Translate } from 'common'
import { totalProductsQtyInCart } from 'cart'
import { observer } from 'mobx-react-lite'
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import impersonationImgDesktop from 'src/assets/images/tabler_ghost.png'
import impersonationImgMobile from 'src/assets/images/white_tabler_ghost.svg'
import { RootState } from 'store'
import styled from 'styled-components'
import { useUiStateStore } from '../../../../../store/uiState/UiStateStore'
import { useUserStore } from '../../../../../store/user/UserStore'
import { breakpoints } from '../../../../../theme/breakpoints'
import { Tooltip } from '../../../../../ui-components/util/MUITooltipCustomization'
import { ShoppingCartMenu } from '../CommonMenuItems'
import { ProfileMenu } from './profileMenu'

const iconStyle = { fontSize: 33, color: '#000' }
const iconStyleMobile = { fontSize: 25, color: '#fff' }
const ACCOUNT_MOBILE_HEADER_WIDTH = '72px' // the size of the account icon + the size of the arrow down

const AccountMenu = observer((): ReactElement => {
  const [showAccountMenu, setShowAccountMenu] = useState(false)
  const { setShowContactDrawer } = useUiStateStore()
  const userStore = useUserStore()
  const menuButton = useRef<HTMLDivElement>(null)
  const [showAccountNameInfo, setShowAccountNameInfo] = useState(true)

  const contactUStooltip = Translate('contactUs')

  const handleResize = useCallback(() => {
    const width = menuButton?.current?.clientWidth
    setShowAccountNameInfo(width > 150)
  }, [])

  useEffect(() => {
    const resize = debounce(handleResize, 100)
    resize() // Initial call to set things up
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [handleResize])

  const totalQtyInCart: number = useSelector((state: RootState) =>
    totalProductsQtyInCart(state)
  )

  return (
    <Wrapper>
      <UserWrapper
        ref={menuButton}
        onClick={() => setShowAccountMenu(true)}
        width={!showAccountNameInfo ? ACCOUNT_MOBILE_HEADER_WIDTH : null}
      >
        {userStore.getIsImpersonating() ? (
          <>
            <ImpersonateImgMobile
              src={impersonationImgMobile}
              alt="user impersonating"
              className="d-lg-none"
            />
            <img
              src={impersonationImgDesktop}
              alt="user impersonating"
              className="d-none d-lg-block mx-2"
            />
          </>
        ) : (
          <AccountCircleIconButton aria-label="AccountCircle">
            <AccountCircleIcon style={iconStyle} />
            <AccountCircleIconMobile style={iconStyleMobile} />
          </AccountCircleIconButton>
        )}
        {showAccountNameInfo ? (
          <AccountInfo>
            <AccountNameWrapper>
              <UsernameText className="test-class">
                <span>{userStore?.preferences?.orgName} </span>
              </UsernameText>
              <ExpandMoreWrapper>
                {!showAccountMenu ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
              </ExpandMoreWrapper>
            </AccountNameWrapper>
            <OrganizationNameText>
              {userStore?.preferences?.screenName}
            </OrganizationNameText>
          </AccountInfo>
        ) : (
          <ExpandMoreWrapper>
            {!showAccountMenu ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
          </ExpandMoreWrapper>
        )}
      </UserWrapper>

      <LocalPhoneIconButton
        aria-label="LocalPhone"
        onClick={() => setShowContactDrawer(true)}
      >
        <LocalPhone style={iconStyleMobile} />
      </LocalPhoneIconButton>

      <LocalPhoneIconButtonForWeb
        aria-label="LocalPhoneWeb"
        onClick={() => setShowContactDrawer(true)}
      >
        <Tooltip title={contactUStooltip} placement="bottom" arrow>
          <LocalPhone style={iconStyle} />
        </Tooltip>
      </LocalPhoneIconButtonForWeb>

      <ShoppingCartWrapper>
        <ShoppingCartMenu qty={totalQtyInCart} />
      </ShoppingCartWrapper>

      <ProfileMenu
        onClose={() => setShowAccountMenu(false)}
        parentRef={menuButton}
        open={showAccountMenu}
        accountName={userStore?.preferences?.orgName}
      />
    </Wrapper>
  )
})

export default AccountMenu

const Wrapper = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 24px;
  width: 100%;
`

const ExpandMoreWrapper = styled.div`
  margin-top: 2px;
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    display: none;
  }
`

const AccountInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  cursor: pointer;
  @media screen and (${breakpoints.laptop}px < width <= ${breakpoints.laptopMedium}px) {
    min-width: 100px;
  }

  @media screen and (width <= ${breakpoints.tabletLarge}px) {
    display: none;
  }
`

const AccountNameWrapper = styled.div`
  width: 100%;
  display: flex;
`

const UsernameText = styled.span`
  font-family: Oswald, serif;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 27px;
  max-width: 165px;
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const AccountCircleIconButton = styled(IconButton)`
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    padding: 8px 0px;
  }
`

const ImpersonateImgMobile = styled.img`
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    padding: 8px 0px;
  }
`

const AccountCircleIcon = styled(AccountCircle)`
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    display: none;
  }
`

const AccountCircleIconMobile = styled(AccountCircle)`
  display: none;
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    display: block;
  }
`

const LocalPhoneIconButton = styled(IconButton)`
  display: none;
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    display: block;
    padding: 8px 0px;
  }
`

const OrganizationNameText = styled.div`
  font-family: 'Roboto', serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  @media screen and (max-width: ${breakpoints.tabletXLarge}px) {
    display: none;
  }
`

interface UserWrapperProps {
  width?: string
}

const UserWrapper = styled.div<UserWrapperProps>`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
  position: relative;
  cursor: pointer;

  @media screen and (width > ${breakpoints.laptopXLarge}px) {
    width: 261px;
  }

  @media screen and (${breakpoints.laptopLarge}px < width <= ${breakpoints.laptopXLarge}px) {
    min-width: 261px;
    max-width: 261px;
  }

  @media screen and (${breakpoints.laptopMedium}px < width <= ${breakpoints.laptopLarge}px) {
    min-width: 200px;
    max-width: 261px;
  }

  @media screen and (width <= ${breakpoints.laptopMedium}px) {
    ${({ width }) => (width ? `width: ${width};` : `min-width: 100px;`)}
    max-width: 261px;
  }

  @media screen and (width < ${breakpoints.tabletLarge}px) {
    width: fit-content;
  }
`

const LocalPhoneIconButtonForWeb = styled(IconButton)`
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    display: none;
  }
`

const ShoppingCartWrapper = styled.div`
  width: fit-content;
`
